import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react'
import { SingUpWithGoogle } from '../../utils/FirebaseUtils.ts'
import {
  useCreateGuestUserMutation,
  useGenerateSignUpLinkMutation,
  useScheduleRoleMutation,
} from '../../features/SpeechAgendaApiSlice.ts'
import { useDispatch } from 'react-redux'
import { setNeedsRegistration } from '../../features/FirebaseAuthSlice.ts'
import { useState } from 'react'
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { NavLink } from 'react-router'
import { AssignRoleResponse } from '../../generated'
import { formatAssignRoleResponse } from '../../utils/FormatterUtils.ts'

export type RegistrationStep =
  | 'EMAIL'
  | 'NAME'
  | 'COMPLETE_WITH_LINK'
  | 'COMPLETE_WITH_GOOGLE'
  | 'ERROR_SIGN_UP_WITH_LINK'
  | 'ERROR_SIGN_UP_WITH_GOOGLE'

export default function SignUpModal({
  isOpen,
  onClose,
  onTitle,
  onDescription,
  clubId,
  meetingTimestamp,
}: {
  isOpen: boolean
  onClose: (status: RegistrationStep) => void
  onTitle: (title: string) => void
  onDescription: (description: string) => void
  clubId: number
  meetingTimestamp: number
}) {
  const dispatch = useDispatch()
  const [createGuestUser] = useCreateGuestUserMutation()
  const [generateSignUpLink] = useGenerateSignUpLinkMutation()
  const [scheduleRole] = useScheduleRoleMutation()

  const [email, setEmail] = useState('')
  const [name, setName] = useState('')

  const [registrationStep, setRegistrationStep] =
    useState<RegistrationStep>('EMAIL')

  const handleSignUpWithGoogle = async () => {
    dispatch(setNeedsRegistration(true))
    const userDetails = await SingUpWithGoogle()

    try {
      await createGuestUser({
        accessToken: userDetails.accessToken,
        body: {
          name: userDetails.name,
          email: userDetails.email,
          clubId,
        },
      }).unwrap()
    } catch {
      setRegistrationStep('ERROR_SIGN_UP_WITH_GOOGLE')
    }

    dispatch(setNeedsRegistration(false))

    const ROLE_ID_GUEST: number = 11

    let scheduledRoleResponse: AssignRoleResponse
    try {
      scheduledRoleResponse = await scheduleRole({
        clubId: clubId.toString(),
        meetingTimestampUtc: meetingTimestamp.toString(),
        body: { roleId: ROLE_ID_GUEST },
      }).unwrap()
    } catch {
      setRegistrationStep('ERROR_SIGN_UP_WITH_GOOGLE')
      return
    }
    const { title, description } = formatAssignRoleResponse(
      scheduledRoleResponse
    )
    onTitle(title)
    onDescription(description)
    setRegistrationStep('COMPLETE_WITH_GOOGLE')
    onClose('COMPLETE_WITH_GOOGLE')
    setTimeout(() => {
      setRegistrationStep('EMAIL')
    })
  }

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        onClose('EMAIL')
      }}
      className="relative z-10">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500/75 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in"
      />
      <div className="fixed inset-0 w-screen overflow-y-auto z-10">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel>
            <div className="bg-white px-6 py-12 shadow-sm sm:rounded-lg sm:px-12 max-w-80">
              {/*Internal content of the modal*/}
              {registrationStep !== 'COMPLETE_WITH_LINK' &&
                registrationStep !== 'COMPLETE_WITH_GOOGLE' &&
                registrationStep !== 'ERROR_SIGN_UP_WITH_LINK' && (
                  <DialogTitle
                    as="h3"
                    className="text-base font-semibold text-gray-900 mb-3">
                    Create an Account to sign up easily
                  </DialogTitle>
                )}
              {registrationStep === 'EMAIL' && (
                <button
                  onClick={handleSignUpWithGoogle}
                  className="flex w-full items-center justify-center gap-3 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 shadow-xs ring-gray-300 ring-inset hover:bg-gray-50 focus-visible:ring-transparent">
                  <svg
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                    className="h-5 w-5">
                    <path
                      d="M12.0003 4.75C13.7703 4.75 15.3553 5.36002 16.6053 6.54998L20.0303 3.125C17.9502 1.19 15.2353 0 12.0003 0C7.31028 0 3.25527 2.69 1.28027 6.60998L5.27028 9.70498C6.21525 6.86002 8.87028 4.75 12.0003 4.75Z"
                      fill="#EA4335"
                    />
                    <path
                      d="M23.49 12.275C23.49 11.49 23.415 10.73 23.3 10H12V14.51H18.47C18.18 15.99 17.34 17.25 16.08 18.1L19.945 21.1C22.2 19.01 23.49 15.92 23.49 12.275Z"
                      fill="#4285F4"
                    />
                    <path
                      d="M5.26498 14.2949C5.02498 13.5699 4.88501 12.7999 4.88501 11.9999C4.88501 11.1999 5.01998 10.4299 5.26498 9.7049L1.275 6.60986C0.46 8.22986 0 10.0599 0 11.9999C0 13.9399 0.46 15.7699 1.28 17.3899L5.26498 14.2949Z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12.0004 24.0001C15.2404 24.0001 17.9654 22.935 19.9454 21.095L16.0804 18.095C15.0054 18.82 13.6204 19.245 12.0004 19.245C8.8704 19.245 6.21537 17.135 5.2654 14.29L1.27539 17.385C3.25539 21.31 7.3104 24.0001 12.0004 24.0001Z"
                      fill="#34A853"
                    />
                  </svg>
                  <span className="text-sm/6 font-semibold">
                    Continue with Google
                  </span>
                </button>
              )}
              {registrationStep === 'EMAIL' && (
                <div className="relative mt-5 mb-5">
                  <div
                    aria-hidden="true"
                    className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200" />
                  </div>
                  <div className="relative flex justify-center text-sm/6 font-medium">
                    <span className="bg-white px-6 text-gray-900">
                      Or continue with email
                    </span>
                  </div>
                </div>
              )}
              <div className="space-y-6 text-left">
                {/* EMAIL */}
                {registrationStep == 'EMAIL' && (
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm/6 font-medium text-gray-900">
                      Email address
                    </label>
                    <div className="mt-2">
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        autoComplete="email"
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      />
                    </div>
                  </div>
                )}
                {registrationStep == 'EMAIL' && (
                  <div>
                    <button
                      type="submit"
                      onClick={() => {
                        setEmail(email)
                        setRegistrationStep('NAME')
                      }}
                      className="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm/6 font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                      Continue
                    </button>
                  </div>
                )}
                {/* NAME */}
                {registrationStep == 'NAME' && (
                  <div>
                    <label
                      htmlFor="fullName"
                      className="block text-sm/6 font-medium text-gray-900">
                      Full name
                    </label>
                    <div className="mt-2">
                      <input
                        id="fullName"
                        name="fullName"
                        type="text"
                        required
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        autoComplete="name"
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      />
                    </div>
                  </div>
                )}
                {/*Save name*/}
                {registrationStep == 'NAME' && (
                  <div>
                    <button
                      type="submit"
                      onClick={async () => {
                        try {
                          await generateSignUpLink({
                            email,
                            name,
                            clubId,
                            meetingTimestamp,
                          })
                          setRegistrationStep('COMPLETE_WITH_LINK')
                        } catch {
                          setRegistrationStep('ERROR_SIGN_UP_WITH_LINK')
                        }
                      }}
                      className="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm/6 font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                      Continue
                    </button>
                  </div>
                )}
                {/* Back */}
                {registrationStep == 'NAME' && (
                  <div>
                    <button
                      type="submit"
                      onClick={() => {
                        setRegistrationStep('EMAIL')
                      }}
                      className="flex w-full justify-center rounded-md bg-gray-800 px-3 py-1.5 text-sm/6 font-semibold text-white shadow-xs hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                      Back
                    </button>
                  </div>
                )}
                {registrationStep == 'EMAIL' && (
                  <div className="flex items-center justify-center">
                    <div className="text-sm/6 flex items-center justify-center gap-1">
                      <p>Already have an account?</p>
                      <NavLink
                        to="/login"
                        className="font-semibold text-indigo-600 hover:text-indigo-500">
                        Sign In
                      </NavLink>
                    </div>
                  </div>
                )}
                {registrationStep == 'COMPLETE_WITH_LINK' && (
                  <div>
                    <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-green-100">
                      <CheckIcon
                        aria-hidden="true"
                        className="size-6 text-green-600"
                      />
                    </div>
                    <div className="mt-3 text-center sm:mt-5">
                      <DialogTitle
                        as="h3"
                        className="text-base font-semibold text-gray-900">
                        Account Activation Link Sent
                      </DialogTitle>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          We've sent a sign-up link to your email address.
                          Please check your inbox and click the link to complete
                          your registration
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                {registrationStep == 'ERROR_SIGN_UP_WITH_GOOGLE' && (
                  <div>
                    <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-red-100">
                      <XMarkIcon
                        aria-hidden="true"
                        className="size-6 text-red-600"
                      />
                    </div>
                    <div className="mt-3 text-center sm:mt-5">
                      <DialogTitle
                        as="h3"
                        className="text-base font-semibold text-gray-900">
                        Error Occurred
                      </DialogTitle>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          We couldn't complete your sign up with google. Please
                          try signing up with another method, or contact our
                          support team if you continue experiencing issues.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                {registrationStep == 'ERROR_SIGN_UP_WITH_LINK' && (
                  <div>
                    <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-red-100">
                      <XMarkIcon
                        aria-hidden="true"
                        className="size-6 text-red-600"
                      />
                    </div>
                    <div className="mt-3 text-center sm:mt-5">
                      <DialogTitle
                        as="h3"
                        className="text-base font-semibold text-gray-900">
                        Error Occurred
                      </DialogTitle>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          We couldn't complete your registration via email link.
                          Please try signing up with another method, or contact
                          our support team if you continue experiencing issues.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                {registrationStep == 'ERROR_SIGN_UP_WITH_LINK' ||
                  (registrationStep == 'ERROR_SIGN_UP_WITH_GOOGLE' && (
                    <div>
                      <button
                        type="submit"
                        onClick={() => {
                          setRegistrationStep('EMAIL')
                        }}
                        className="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm/6 font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                        Go back
                      </button>
                    </div>
                  ))}
                {registrationStep == 'COMPLETE_WITH_LINK' && (
                  <div>
                    <button
                      type="submit"
                      onClick={() => {
                        onClose('COMPLETE_WITH_LINK')
                        setTimeout(() => {
                          setRegistrationStep('EMAIL')
                        })
                      }}
                      className="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm/6 font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                      Go back to meeting details
                    </button>
                  </div>
                )}
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  )
}
